import Image from 'next/image';
import cn from 'classnames';
import Container from '@components/global/Container';
import Button from '@components/ui/Button';
import Heading from '@components/ui/Heading';
import {
  ImageBannerSliceData,
  ImageBannerTextAlignment,
} from '@adapters/prismic/types/slices';
import { eePromoImpHandler, setPromoDetailsCookie } from '@utils/gtm';
import { useEffect, useMemo, useRef } from 'react';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { useLocale } from '@hooks/useLocale';
import styles from './ImageBanner.module.scss';

const ImageBanner = ({
  image,
  title,
  content,
  text_alignment,
  button_text,
  button_link,
  trackPromos,
  pageType,
  sliceIndex,
}: ImageBannerSliceData) => {
  const locale = useLocale();
  const ref = useRef<any>(null);
  const isVisible = useObjectVisibility(ref);

  const promoDetails = useMemo(
    () => ({
      id: title,
      name: `${title}-${button_text}`,
      type: 'link',
      url: button_link,
      position: `${pageType}-${sliceIndex}-ImageBanner`,
    }),
    [button_link, button_text, pageType, sliceIndex, title]
  );

  useEffect(() => {
    if (trackPromos && isVisible) {
      eePromoImpHandler(promoDetails);
    }
  }, [isVisible, promoDetails, trackPromos]);

  return (
    <Container>
      <div
        ref={ref}
        className={styles.imageBannerContainer}
        style={{
          padding: `calc(25% * ${image.dimensions.height}/${image.dimensions.width}) 0`,
        }}
      >
        <Image
          src={image.url}
          alt={image.alt || title}
          quality={65}
          fill
          sizes="(min-width: 1260px) 1260px, 100vw"
        />
        <div
          className={cn(
            styles.imageBannerContent,
            text_alignment === ImageBannerTextAlignment.Center
              ? styles.imageBannerContentCenterAlign
              : ''
          )}
        >
          <Heading
            semanticLevel="h2"
            styleLevel="2"
            className={styles.imageBannerHeading}
          >
            {title}
          </Heading>
          <Heading
            semanticLevel="h3"
            styleLevel="6"
            className={styles.imageBannerDescriptionContent}
          >
            {content}
          </Heading>
          {button_link && button_text && (
            <Button
              label={button_text}
              size="large"
              variant="secondary"
              type="button"
              href={`/${locale}${button_link}`}
              handleClick={
                trackPromos
                  ? () => setPromoDetailsCookie(promoDetails)
                  : undefined
              }
            />
          )}
        </div>
      </div>
    </Container>
  );
};

export default ImageBanner;
